import type { ApolloError } from '@apollo/client';
import type {
  FormikConfig,
  FormikErrors,
  FormikHelpers,
  FormikValues,
} from 'formik';
import { Formik, setIn } from 'formik';
import type { GraphQLFormattedError } from 'graphql';
import * as inflection from 'inflection';
import { merge } from 'lodash-es';
import React, { useRef } from 'react';
import type { SetOptional } from 'type-fest';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { UIActions } from '../ui/uiSlice';
import parseGraphQLError from '../utils/error/parseGraphQLError';
import parseValidationError from '../utils/error/parseValidationError';

export { FieldArray } from 'formik';
export { default as FieldSet } from './formInternals/FieldSet';
export { default as Field } from './formInternals/FormikFormField';
export { default as Submit } from './formInternals/FormikSubmitButton';

const UNKNOWN_ERROR_MESSAGE = 'Oops, something went wrong. Please try again.';

type FormProps<Values> = SetOptional<FormikConfig<Values>, 'onSubmit'> & {
  apiErrorMessages?: { [code: string]: string };
  apiErrorHandlers?: {
    [code: string]: (
      error: GraphQLFormattedError,
      formik: FormikHelpers<Values>,
    ) => void;
  };
};

const Form = <Values extends FormikValues = FormikValues>({
  apiErrorMessages = {},
  apiErrorHandlers = {},
  validate,
  onSubmit,
  children,
  ...formikProps
}: FormProps<Values>) => {
  const dispatch = useAppDispatch();
  const formElement = useRef<HTMLFormElement | null>(null);

  const getErrorHandler = (formik: FormikHelpers<Values>) => {
    const showError = (message: string) => {
      dispatch(UIActions.pushToast({ level: 'error', message }));
    };

    return (errors: ApolloError) => {
      if (!errors.graphQLErrors) {
        throw errors;
      }

      const { graphQLErrors } = errors;
      const { fieldErrors, unhandledErrors } = graphQLErrors.reduce<{
        fieldErrors: FormikErrors<Values>;
        unhandledErrors: GraphQLFormattedError[];
      }>(
        (memo, error) => {
          const { code } = parseGraphQLError(error);

          if (code) {
            const errorHandler = apiErrorHandlers[code];
            if (errorHandler) {
              errorHandler(error, formik);
              return memo;
            }

            const errorMessage = apiErrorMessages[code];
            if (errorMessage) {
              showError(errorMessage);
              return memo;
            }
          }

          const validationError = parseValidationError(error);

          if (validationError) {
            return {
              ...memo,
              fieldErrors: {
                ...memo.fieldErrors,
                [validationError.field]: inflection.capitalize(
                  validationError.message,
                ),
              },
            };
          }

          return {
            ...memo,
            unhandledErrors: [...memo.unhandledErrors, error],
          };
        },
        { fieldErrors: {}, unhandledErrors: [] },
      );

      if (Object.keys(fieldErrors).length) {
        formik.setErrors(fieldErrors);
      }

      if (unhandledErrors.length) {
        showError(UNKNOWN_ERROR_MESSAGE);
      }

      // TODO handle network errors
    };
  };

  const handleValidate = async (values: Values) => {
    const errors = validate?.(values);
    if (!formElement.current) {
      return errors;
    }

    // Validation function is triggered by formik state change, which might not be already reflected in the HTML input value.
    // Therefore we need to wait for it to be updated.
    await Promise.resolve();

    let browserErrors = {};
    const inputs = formElement.current.elements;

    [...inputs].forEach((input) => {
      const name =
        'name' in input && typeof input.name === 'string' ? input.name : null;
      const validationMessage =
        'validationMessage' in input ? input.validationMessage : null;
      if (name && validationMessage) {
        browserErrors = setIn(browserErrors, name, validationMessage);
      }
    });

    return merge({}, browserErrors, errors);
  };

  const handleSubmit = (values: Values, formik: FormikHelpers<Values>) => {
    const promise = onSubmit?.(values, formik);

    if (promise?.catch) {
      return promise.catch(getErrorHandler(formik)).then(() => {
        formik.setSubmitting(false);
      });
    }

    formik.setSubmitting(false);
    return promise;
  };

  return (
    <Formik<Values>
      {...formikProps}
      validate={handleValidate}
      onSubmit={handleSubmit}
    >
      {(renderProps) => (
        <form
          onSubmit={renderProps.handleSubmit}
          ref={(form) => {
            formElement.current = form;
          }}
          noValidate
        >
          {typeof children === 'function' ? children(renderProps) : children}
        </form>
      )}
    </Formik>
  );
};

export default Form;
